import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RichTextEditor from './RichTextEditor';
import Barcode from 'react-barcode'
import { createNewReport, getAllReportTemplates, getCurrentReport, UpdatedReportData } from '@custom/Apis/apis';
import { useTheme } from "../../ui/src/contextProviders";
import { calculateAge, capitalizeWords, customStyle, formatDate } from '@custom/Utils/utils';
import Header from '@custom/Header/Header';
import { servicesManager } from '../../app/src/App';
import { _reportStatus, _roleType } from '../../custom/Utils/constants/appConstants';
import { Loader } from '@ohif/ui';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@custom/store/store';
import hospitalDetailApi from '@custom/rtk-apis/hospital/hospitalDetails';
import userDetails from '@custom/rtk-apis/user/userDetails';
import SpeechToText from '@custom/Common/SpeechToText';

interface createReportProps {
  reportStatus?: any;
  isPathology?: any;
  imageBlob?: any;
  selectedTempleate?: any;
  selectedTemplateName?: any;
  reportId?: any;
  impressionsProp?: string;
}

const CreateReport = (props: createReportProps) => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location || {};
  const role = sessionStorage.getItem('role') || localStorage.getItem('role');
  const dispatch = useDispatch<AppDispatch>();
  const { uiNotificationService } = servicesManager.services;
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState("");
  const [isPopupOpen, setAuthorizedPopup] = useState(false);
  const [selectedReportStatus, setSelectedReport] = useState("");
  const [openReportPanel, setOpenReportPanel] = useState(true);
  const [currentReport, setCurrentReport] = useState(null);
  const [speechToTextAvailable, setSpeechToTextAvailable] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState({
    label: props.reportStatus || currentReport?.status || _reportStatus.Draft,
    value: props.reportStatus || currentReport?.status || _reportStatus.Draft
  });
  const [userImpressions, setUserImpressions] = useState<string>(props.impressionsProp || "");
  const [autoCreatedReportId, setAutoCreatedReportId] = useState('');

  useEffect(() => {
    if (selectedTemplate?.impressions) {
      setUserImpressions(selectedTemplate.impressions);
    }
  }, [selectedTemplate]);

  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoading(true);
      try {
        const response = await getAllReportTemplates({});
        if (response?.status === 'SUCCESS') {
          const templates = response.templateList;
          setTemplates(templates);

          if (state?.data?.modality) {
            const filtered = templates.filter((template) =>
              template.modality.toLowerCase().includes(
                state.data.modality.toLowerCase()
              ) ||
              state.data.modality.toLowerCase().includes(
                template.modality.toLowerCase()
              )
            );
            setFilteredTemplates(filtered);
          } else {
            setFilteredTemplates(templates);
          }
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, [state?.data?.modality]);

  useEffect(() => {
    if (openReportPanel === false) {
      navigate('/appointments');
    }
  }, [openReportPanel]);

  useEffect(() => {
    const checkSpeechToTextAvailability = async () => {
      try {
        const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
        const [hospitalDetail, userDetail] = await Promise.all([
          dispatch(hospitalDetailApi({ hospitalId })).unwrap(),
          dispatch(userDetails()).unwrap()
        ]);
        setSpeechToTextAvailable(
          hospitalDetail?.result?.addOnsAvailable?.speechToText === true &&
          userDetail?.result?.speechToText === true
        );
      } catch (err) {
        console.error("Error in checkSpeechToTextService", err);
        setSpeechToTextAvailable(false);
      }
    };

    checkSpeechToTextAvailability();
  }, [dispatch]);

  const handleSearchChange = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(searchValue);
    setFilteredTemplates(
      templates.filter((template) =>
        template.templateName.toLowerCase().includes(searchValue)
      )
    );
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setIsDropdownOpen(false);
  };

  const handleImpressionsChange = (value: string) => {
    setUserImpressions(value);
  };

  const checkPermissionByRole = () => {
    return role === _roleType.pathologist || role === _roleType.radiologist || role === _roleType.hospitalSuperadmin || role === _roleType.admin;
  };

  useEffect(() => {
    const fetchCurrentReport = async () => {
      setIsLoading(true);
      try {
        const studyInstanceUID = state?.data?.patientInfo?.patientId;
        const payload = { studyInstanceUID };
        const response = await getCurrentReport(payload);
        setAutoCreatedReportId(response?.result?._id);

        if (response?.status === 'SUCCESS') {
          setCurrentReport(response.result);
          setSelectedTemplate(response.result);
        } else {
          console.error('Failed to fetch current report:', response?.msg);
        }
      } catch (error) {
        console.error('Error fetching current report:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrentReport();
  }, []);

  const handleCreateReport = async (selectedHTML, status, comment, updatedImpressions, buttonClick) => {
    if (isProcessing || window.location.pathname === '/reportlist') return;
    setIsProcessing(true);

    const formattedStatus = capitalizeWords(status);
    setStatus(formattedStatus);
    // setIsLoading(true);

    const commentId = document.getElementById('comment');
    const impressionId = document.getElementById('impression');

    try {
      if (formattedStatus.toLowerCase() === 'authorized') {
        setAuthorizedPopup(true);
        setReportData(selectedHTML);
        setSelectedReport(formattedStatus);
      }

      const impression = updatedImpressions === undefined ? userImpressions : updatedImpressions || selectedTemplate?.impressions || '';
      if (currentReport === null || currentReport?.length === 0) {
        const response = await createNewReport({
          htmlTemplate: selectedHTML,
          editorMode: state?.data?.editorMode,
          patientName: `${state?.data?.patientInfo?.firstName} ${state?.data?.patientInfo?.lastName}`,
          patientGender: state?.data?.patientInfo?.gender === 'M' ? 'Male' : 'Female',
          reportName: selectedTemplate?.templateName,
          patientAge: calculateAge(state?.data?.patientInfo?.dob) || 'N/A',
          patientID: state?.data?.patientInfo?.patientId,
          uhid: state?.data?.patientInfo?.UHID,
          date: new Date().toISOString().split('T')[0],
          time: new Date().toTimeString().split(' ')[0],
          studyInstanceUID: state?.data?.patientInfo?.patientId,
          status: formattedStatus,
          images: [],
          modality: state?.data?.modality,
          comment: commentId?.value || comment,
          impressions: impressionId?.value || impression,
          templateId: selectedTemplate?._id,
        });
        setAutoCreatedReportId(response?.result?.id);
        if(!buttonClick){
          window.dispatchEvent(new CustomEvent('report-autosaved'));
          uiNotificationService.show({
            title: 'Success',
            message: response?.msg || "Report created successfully",
            type: 'success',
            duration: 3000,
            position: 'topRight',
          });
        } else {
          navigate('/reportlist');
        }
      } else {
        const updatePayload = {
          templateHTML: selectedHTML,
          _id: selectedTemplate?._id,
          status: status,
          comment: comment,
          impressions: impression,
          templateId: selectedTemplate?.templateId,
          patientID: state?.data?.patientInfo?.patientId,
        };
        const response = await UpdatedReportData(updatePayload);

        if (response?.res?.ok) {
          setAutoCreatedReportId(response?.res?.id);
          if(!buttonClick){
            window.dispatchEvent(new CustomEvent('report-autosaved'));
          } else {
            navigate('/reportlist');
          }
          uiNotificationService.show({
            title: 'Success',
            message: response?.msg || "Report updated successfully",
            type: 'success',
            duration: 3000,
            position: 'topRight',
          });
        }
      }
    } catch (error) {
      console.error('Error creating/updating report:', error);
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to process the report',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  }

  return (
    <>
      <div
        className="page page__full"
        style={isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}
      >
        <Header title={!currentReport || currentReport?.length === 0 ? 'Generate Report' : 'View Report'} isBack={true} />
        <div className="page__body">
          <div className="details">
            <p className="bg-[#51A196] text-white font-semibold py-2 px-5 text-lg">
              {!currentReport || currentReport?.length === 0 ? 'Select Template to Generate Report' : selectedTemplate?.templateName}
            </p>
            <section className='px-2 py-4'>
              <div className="flex flex-row" style={{ columnGap: "1rem" }}>

                <div className=" flex flex-col justify-start w-1/4">
                  <div className="flex flex-row gap-6">
                    <div className="flex-none">
                      <p className='text-[24px] font-semibold mb-4 text-[#49475A]'>
                        {state?.data?.patientInfo?.firstName
                          ? capitalizeWords(state?.data?.patientInfo?.firstName) + ' ' + capitalizeWords(state?.data?.patientInfo?.lastName)
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                          : '---'}
                      </p>
                      <p className='text-[13px] font-semibold'>
                        <span className='text-[#49475A]'>Patient ID :</span>
                        <span className='font-normal ml-2'>{state?.data?.patientInfo?.patientId || "---"}</span>
                      </p>
                    </div>
                    <div className="flex-1 mt-1">
                      <p className='text-[13px] font-semibold mb-4'>
                        <span className='text-[#49475A]'>Date :</span>
                        <span className='font-normal ml-2'>{formatDate(state?.data?.date) || "---"}</span>
                      </p>
                      <p className='text-[13px] font-semibold'>
                        <span className='text-[#49475A]'>Gender :</span>
                        <span className='font-normal ml-2'>{state?.data?.patientInfo?.gender || "---"}</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex">
                  <div className='border-l-2 border-[#51A196] mx-2 h-full' style={{ width: "2px", backgroundColor: "#51A196" }}></div>
                </div>

                <div className="w-1/4 flex flex-col justify-start mt-1">
                  <div className="flex flex-row gap-6">
                    <div className="flex-none">
                      <p className='text-[13px] font-semibold mb-4'>
                        <span className={`${isDarkMode?.isDarkMode ? "text-[#ffffff]" : 'text-[#49475A]'}`}>Age :</span>
                        <span className={`font-normal ml-2  ${isDarkMode?.isDarkMode ? "text-[#ffffff]" : ""}`}>{state?.data?.patientInfo?.dob ? calculateAge(state?.data?.patientInfo?.dob) : "---"}</span>
                      </p>
                      <p className='text-[13px] font-semibold'>
                        <span className={`${isDarkMode?.isDarkMode ? "text-[#ffffff]" : 'text-[#49475A]'}`}>Blood Group :</span>
                        <span className={`font-normal ml-2  ${isDarkMode?.isDarkMode ? "text-[#ffffff]" : ""}`}>{state?.data?.patientInfo?.bloodGroup || "---"}</span>
                      </p>
                    </div>
                    <div className="flex-1 flex flex-col justify-start">
                      <p className='text-[13px] font-semibold mb-4'>
                        <span className={`${isDarkMode?.isDarkMode ? "text-[#ffffff]" : 'text-[#49475A]'}`}>Ph No. :</span>
                        <span className={`font-normal ml-2  ${isDarkMode?.isDarkMode ? "text-[#ffffff]" : ""}`}>
                          {state?.data?.patientInfo
                            ? `${state?.data?.patientInfo?.contactInfo?.dialCode} ${state?.data?.patientInfo?.contactInfo?.mobileNo}`
                            : "---"}
                        </span>
                      </p>
                      <p className='text-[13px] font-semibold'>
                        <span className={`${isDarkMode?.isDarkMode ? "text-[#ffffff]" : 'text-[#49475A]'}`}>WhatsApp No. :</span>
                        <span className={`font-normal ml-2  ${isDarkMode?.isDarkMode ? "text-[#ffffff]" : ""}`}>
                          {state?.data?.patientInfo
                            ? `${state?.data?.patientInfo?.contactInfo?.whatsappNoDialCode} ${state?.data?.patientInfo?.contactInfo?.whatsappNo}`
                            : "---"}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex">
                  <div className='border-l-2 border-[#51A196] mx-2 h-full' style={{ width: "2px", backgroundColor: "#51A196" }}></div>
                </div>

                <div className="w-1/2 flex flex-col justify-start">
                  <div className="flex flex-row gap-6">
                    <div className="flex-none flex flex-col justify-start mt-1">
                      <p className='text-[13px] font-semibold mb-4'>
                        <span className={`${isDarkMode?.isDarkMode ? "text-[#ffffff]" : 'text-[#49475A]'}`}>Height :</span>
                        <span className={`font-normal ml-2  ${isDarkMode?.isDarkMode ? "text-[#ffffff]" : ""}`}>
                          {state?.data?.patientInfo?.height ? state?.data?.patientInfo?.height + ' ' + 'cm' : "---"}
                        </span>
                      </p>
                      <p className='text-[13px] font-semibold'>
                        <span className={`${isDarkMode?.isDarkMode ? "text-[#ffffff]" : 'text-[#49475A]'}`}>Weight :</span>
                        <span className={`font-normal ml-2  ${isDarkMode?.isDarkMode ? "text-[#ffffff]" : ""}`}>
                          {state?.data?.patientInfo?.weight ? state?.data?.patientInfo?.weight + ' ' + 'kg' : "---"}
                        </span>
                      </p>
                    </div>
                    <div className="flex-1 flex flex-col justify-start mt-1">
                      <p className='text-[13px] font-semibold mb-4'>
                        <span className={`${isDarkMode?.isDarkMode ? "text-[#ffffff]" : 'text-[#49475A]'}`}>Consulting Doctor :</span>
                        <span className={`font-normal ml-2  ${isDarkMode?.isDarkMode ? "text-[#ffffff]" : ""}`}>
                          {state?.data?.doctorName || "---"}
                        </span>
                      </p>
                      <p className='text-[13px] font-semibold'>
                        <span className={`${isDarkMode?.isDarkMode ? "text-[#ffffff]" : 'text-[#49475A]'}`}>Modality Type :</span>
                        <span className={`font-normal ml-2  ${isDarkMode?.isDarkMode ? "text-[#ffffff]" : ""}`}>{state?.data?.modality || "---"}</span>
                      </p>
                    </div>
                    <div className="flex-1 text-center items-center flex flex-col justify-center">
                      <p className='text-[13px] font-semibold mb-1'>
                        <span className={`${isDarkMode?.isDarkMode ? "text-[#ffffff]" : 'text-[#49475A]'}`}>UHID :</span>
                        <span className={`font-normal ml-2  ${isDarkMode?.isDarkMode ? "text-[#ffffff]" : ""}`}>{state?.data?.patientInfo?.UHID || "---"}</span>
                      </p>
                      {state?.data?.patientInfo?.UHID && <Barcode displayValue={false} width={1.2} height={50} value={state?.data?.patientInfo?.UHID} />}
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <div className="form form--innersec">
              <div className="form__section">
                {!currentReport || currentReport?.length === 0 ? (
                  <div className="relative max-w-[450px] mx-auto mb-[20px]">
                    <label
                      htmlFor="templateDropdown"
                      className="block mb-2 text-sm font-medium text-gray-700"
                    >
                      Template
                    </label>
                    <div className="relative">
                      <div
                        className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm cursor-pointer"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      >
                        {selectedTemplate?.templateName
                          ? capitalizeWords(selectedTemplate.templateName)
                          : '-- Search or Select --'}
                      </div>
                      {isDropdownOpen && (
                        <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          <input
                            type="text"
                            placeholder="Search templates..."
                            className="block w-full p-2 border-b border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={searchTerm}
                            onChange={handleSearchChange}
                          />
                          <ul className="py-1">
                            {filteredTemplates.length > 0 ? (
                              filteredTemplates.map((template) => (
                                <li
                                  key={template.id}
                                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                  onClick={() => handleTemplateSelect(template)}
                                >
                                  {capitalizeWords(template.templateName)}
                                </li>
                              ))
                            ) : (
                              <li className="px-4 py-2 text-gray-500">No templates found</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  null
                )}
                <div id="content-edit">
                  {currentReport === null ? (
                    <RichTextEditor
                      editorMode={selectedTemplate?.editorMode}
                      selectedItem={selectedTemplate}
                      retractable={true}
                      setSelectedTempleate={setSelectedTemplate}
                      handleCreateReport={handleCreateReport}
                      setOpenReportPanel={setOpenReportPanel}
                      reportStatus={currentReport?.status}
                      reportId={props.reportId}
                      patientId={state?.data?.patientInfo?.patientId}
                      findingsProp={selectedTemplate?.findings}
                      impressionsProp={selectedTemplate?.impressions}
                      commentprop={selectedTemplate?.comment}
                      isPathology={false}
                      onImpressionsChange={handleImpressionsChange}
                      autoCreatedReportId={autoCreatedReportId}
                      setAutoCreatedReportId={setAutoCreatedReportId}
                    />
                  ) : (
                    <RichTextEditor
                      editorMode={selectedTemplate?.editorMode}
                      selectedItem={selectedTemplate}
                      retractable={true}
                      setSelectedTempleate={setSelectedTemplate}
                      handleCreateReport={handleCreateReport}
                      setOpenReportPanel={setOpenReportPanel}
                      reportStatus={currentReport?.status}
                      reportId={props.reportId}
                      patientId={state?.data?.patientInfo?.patientId}
                      findingsProp={selectedTemplate?.findings}
                      impressionsProp={selectedTemplate?.impressions}
                      commentprop={selectedTemplate?.comment}
                      isPathology={false}
                      onImpressionsChange={handleImpressionsChange}
                      autoCreatedReportId={autoCreatedReportId}
                      setAutoCreatedReportId={setAutoCreatedReportId}
                    />
                  )}
                </div>
                {checkPermissionByRole() && speechToTextAvailable && (
                  <SpeechToText contentDiv="content-edit" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Loader isLoaderOpen={isLoading} />
    </>
  );
};

export default CreateReport;
